import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdVisibility, MdRoute, MdLocationOn } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageHeader from '../components/PageHeader';
import '../Admin.css';
import './BookRoom.css';
import './Routes.css';
import apis, { routesApi } from '../../../api/adminapi';
import vehicleApi from '../../../api/vehicle';
import StatusToggle from '../../../Components/StatusToggle';

function Routes() {
  const [showAddRouteModal, setShowAddRouteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const assignedVehicleRef = useRef(null);
  
  const [dropStations, setDropStations] = useState([]);
  const [dropStationInput, setDropStationInput] = useState('');
  const [round, setRound] = useState('');
  const [timing, setTiming] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Using only a top-level error message; no per-field errors
  const [routes, setRoutes] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [tableError, setTableError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateData, setDeactivateData] = useState({ routeId: null, routeLabel: '', reason: '' });
  

  const openAddRoute = () => {
    setShowAddRouteModal(true);
    setError('');
    setSuccessMessage('');
    setIsEditMode(false);
    setEditingRouteId(null);
  };

  const closeAddRoute = () => {
    setShowAddRouteModal(false);
    setSource('');
    setDestination('');
    setAssignedVehicles([]);
    
    setDropStations([]);
    setDropStationInput('');
    setRound('');
    setTiming('');
    setVehicles([]);
    setError('');
    setSuccessMessage('');
    setIsEditMode(false);
    setEditingRouteId(null);
  };


  const addDropStation = (value) => {
    const raw = (value ?? dropStationInput).trim();
    if (!raw) return;
    const exists = dropStations.some(s => s.toLowerCase() === raw.toLowerCase());
    if (!exists) {
      setDropStations((prev) => [...prev, raw]);
    }
    setDropStationInput('');
  };

  const removeDropStation = (station) => {
    setDropStations((prev) => prev.filter((s) => s !== station));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = !source.trim() || !destination.trim() || !round || !timing || assignedVehicles.length === 0;
    if (missing) {
      console.log('missing', missing)
      setError('Please fill all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccessMessage('');
      const formatTimeWithAmPm = (time24) => {
        if (!time24) return undefined;
        const [hhStr, mmStr] = time24.split(':');
        const hour24 = parseInt(hhStr, 10);
        const minute = parseInt(mmStr, 10);
        const inferredMeridiem = !Number.isNaN(hour24) ? (hour24 >= 12 ? 'PM' : 'AM') : '';
        if (Number.isNaN(hour24) || Number.isNaN(minute)) return `${time24} ${inferredMeridiem}`.trim();
        let hour12 = hour24 % 12;
        if (hour12 === 0) hour12 = 12;
        const minutesPadded = String(minute).padStart(2, '0');
        return `${hour12}:${minutesPadded} ${inferredMeridiem}`.trim();
      };
      const payload = { 
        // Compose a routeName to keep backend compatibility
        routeName: `${source.trim()} - ${destination.trim()}`,
        source: source.trim(),
        destination: destination.trim(),
        
        dropLocations: Array.isArray(dropStations) ? dropStations : [],
        round: round || undefined,
        timing: formatTimeWithAmPm(timing) || undefined,
        assignVehicle: Array.isArray(assignedVehicles) ? assignedVehicles : [],
      };
      if (isEditMode && editingRouteId) {
        await apis.routesApi.updateRoute(editingRouteId, payload);
        setSuccessMessage('Route updated successfully');
        toast.success('Route updated successfully');
      } else {
        await apis.routesApi.addRoute(payload);
        setSuccessMessage('Route created successfully');
        toast.success('Route created successfully');
      }
      // Refresh table after successful create
      await loadRoutes();
      closeAddRoute();
    } catch (err) {
      const backendMsg = (typeof err?.response?.data === 'string')
        ? err.response.data
        : err?.response?.data?.message;
      setError(backendMsg || err?.message || (isEditMode ? 'Failed to update route' : 'Failed to create route'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const parseTimeTo24h = (value) => {
    if (!value) return '';
    const cleaned = String(value).replace(/\s+/g, '');
    const ampmMatch = cleaned.match(/^(\d{1,2}):?(\d{2})(AM|PM)$/i);
    if (ampmMatch) {
      let hour = parseInt(ampmMatch[1], 10);
      const minutes = parseInt(ampmMatch[2], 10);
      const meridiem = ampmMatch[3].toUpperCase();
      if (meridiem === 'PM' && hour < 12) hour += 12;
      if (meridiem === 'AM' && hour === 12) hour = 0;
      return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    const hhmm = cleaned.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmm) return `${hhmm[1].padStart(2, '0')}:${hhmm[2]}`;
    return value; // fallback
  };

  const openEditRoute = async (routeId) => {
    try {
      setIsEditMode(true);
      setEditingRouteId(routeId);
      setShowAddRouteModal(true);
      setIsSubmitting(false);
      setError('');
      const data = await routesApi.getRouteById(routeId);
      const r = data?.data || data; // normalize
      const fetchedSource = (r?.source || '').trim();
      const fetchedDestination = (r?.destination || '').trim();
      const fetchedDrops = Array.isArray(r?.dropLocations) ? r.dropLocations : (Array.isArray(r?.dropStations) ? r.dropStations : []);
      const fetchedRound = r?.round ? String(r.round) : '';
      const fetchedTiming = parseTimeTo24h(r?.timing || r?.time || '');
      // Support backend returning either `assignVehicle` (array or comma string)
      // or `assignVehicles` (array).
      let vehiclesStr = [];
      if (Array.isArray(r?.assignVehicle)) {
        vehiclesStr = r.assignVehicle;
      } else if (Array.isArray(r?.assignVehicles)) {
        vehiclesStr = r.assignVehicles;
      } else if (typeof r?.assignVehicle === 'string') {
        vehiclesStr = r.assignVehicle.split(',').map(s => s.trim()).filter(Boolean);
      } else {
        vehiclesStr = [];
      }
      setSource(fetchedSource);
      setDestination(fetchedDestination);
      setDropStations(fetchedDrops);
      setRound(fetchedRound);
      setTiming(fetchedTiming);
      setAssignedVehicles(Array.isArray(vehiclesStr) ? vehiclesStr : []);
      if (fetchedTiming) {
        await loadVehiclesByTiming(fetchedTiming);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load route');
    }
  };

  useEffect(() => {
    loadRoutes();
  }, []);

  

  const loadRoutes = async () => {
    try {
      setIsTableLoading(true);
      setTableError('');
      const data = await apis.routesApi.getAllRoutes();
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (Array.isArray(data?.data)) list = data.data;
      else if (Array.isArray(data?.content)) list = data.content;
      else list = [];
      setRoutes(list);
    } catch (e) {
      setTableError(e?.response?.data?.message || e?.message || 'Failed to fetch routes');
    } finally {
      setIsTableLoading(false);
    }
  };

  const loadVehiclesByTiming = async (selectedTiming) => {
    try {
      const data = await vehicleApi.getAvailableVehiclesByLocationAndTiming(selectedTiming);
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (Array.isArray(data?.data)) list = data.data;
      else if (Array.isArray(data?.content)) list = data.content;
      else list = [];
      setVehicles(list);
    } catch (e) {
      setVehicles([]);
    }
  };

  const handleTimingChange = (e) => {
    const value = e.target.value;
    setTiming(value);
    // Clear vehicles and assigned vehicles when timing changes
    setAssignedVehicles([]);
    setVehicles([]);
  };

  const handleSelectVehicleClick = async () => {
    if (!timing) {
      toast.error('Please select a timing first');
      return;
    }
    // Fetch available vehicles for this timing, location is derived from token
    await loadVehiclesByTiming(timing);
  };

  const filteredRoutes = useMemo(() => routes || [], [routes]);

  const handleRouteStatusToggle = async (route, nextIsActive) => {
    // If trying to deactivate, show confirmation modal requiring reason
    if (!nextIsActive) {
      const src = (route.source || '').trim();
      const dest = (route.destination || '').trim();
      const routeLabel = (src && dest) ? `${src} - ${dest}` : (route.routeName || 'this route');
      setDeactivateData({ routeId: route.id, routeLabel, reason: '' });
      setShowDeactivateModal(true);
      return;
    }

    // Activating can proceed directly
    try {
      await routesApi.updateRouteStatus(route.id, nextIsActive ? 'ACTIVE' : 'INACTIVE');
      await loadRoutes();
    } catch (e) {
      setTableError(e?.response?.data?.message || e?.message || 'Failed to update status');
    }
  };

  const handleRouteDelete = async (route) => {
    setRouteToDelete(route);
  };

  const confirmDeleteRoute = async () => {
    if (!routeToDelete) return;
    try {
      await routesApi.deleteRoute(routeToDelete.id);
      await loadRoutes();
      toast.success('This route is deleted successfully');
    } catch (e) {
      setTableError(e?.response?.data?.message || e?.message || 'Failed to delete route');
      toast.error(e?.response?.data?.message || e?.message || 'Failed to delete route');
    } finally {
      setRouteToDelete(null);
    }
  };

  const cancelDeleteRoute = () => setRouteToDelete(null);

  return (
    <div className="admin-view routes-view">
      <PageHeader breadcrumbs={["Community", "Routes"]} />

      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdRoute className="title-icon" />
            Routes Management
          </h2>
        </div>
        <div className="header-filters-compact">
          <button className="btn-book-compact" onClick={openAddRoute}>
            <MdAdd />
            <span>Add Route</span>
          </button>
        </div>
      </div>

      <div className="table card">
        <div className="table-head">
          <div className="cell">Route</div>
          <div className="cell">Stations</div>
          <div className="cell">Status</div>
          <div className="cell text-right">Actions</div>
        </div>

        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <div className="table-row" key={idx}>
            <div className="cell loading-placeholder">Loading...</div>
            <div className="cell loading-placeholder">Loading...</div>
            <div className="cell loading-placeholder">Loading...</div>
            <div className="cell loading-placeholder">Loading...</div>
          </div>
        ))}

        {!isTableLoading && tableError && (
          <div className="table-row">
            <div className="cell table-error">{tableError}</div>
          </div>
        )}

        {!isTableLoading && !tableError && filteredRoutes && filteredRoutes.length > 0 && filteredRoutes.map((r, idx) => (
          <div className="table-row" key={r.id || r.routeId || idx}>
            <div className="cell">{(() => {
              const src = (r.source || '').trim();
              const dest = (r.destination || '').trim();
              if (src && dest) return `${src} - ${dest}`;
              return r.routeName || '-';
            })()}</div>
            {(() => {
              const stationsArray = Array.isArray(r.dropLocations || r.dropStations)
                ? (r.dropLocations || r.dropStations)
                : [];
              const stationsText = stationsArray.length > 0 ? stationsArray.join(', ') : '-';
              return (
                <div className="cell">{stationsText}</div>
              );
            })()}
            <div className="cell">
              <StatusToggle
                id={`route-status-${r.id || idx}`}
                isActive={(r.status || 'ACTIVE') === 'ACTIVE'}
                onToggle={(next) => handleRouteStatusToggle(r, next)}
              />
            </div>
            <div className="cell actions-cell">
              <button className="btn-action-compact btn-view" onClick={() => { /* Could open view modal; reusing add modal for simplicity */ setIsEditMode(false); setShowAddRouteModal(true); setSource(r.source || ''); setDestination(r.destination || ''); setDropStations(Array.isArray(r.dropLocations) ? r.dropLocations : []); setRound(r.round || ''); setTiming(r.timing || ''); const fromExisting = Array.isArray(r.assignVehicle) ? r.assignVehicle : (Array.isArray(r.assignVehicles) ? r.assignVehicles : (typeof r.assignVehicle === 'string' && r.assignVehicle ? r.assignVehicle.split(',').map(s => s.trim()).filter(Boolean) : [])); setAssignedVehicles(fromExisting); }} title="View">
                <MdVisibility />
              </button>
              <button className="btn-action-compact btn-edit" onClick={() => openEditRoute(r.id || r.routeId)} title="Edit">
                <MdEdit />
              </button>
              <button className="btn-action-compact btn-delete" onClick={() => handleRouteDelete(r)} title="Delete">
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {!isTableLoading && !tableError && (!filteredRoutes || filteredRoutes.length === 0) && (
          <div className="table-row">
            <div className="cell no-results-cell">
              No routes found.
            </div>
          </div>
        )}
      </div>

      {showAddRouteModal && (
        <div className="modal-overlay">
          <div className="modal-content routes-modal">
            <div className="modal-header">
              <h3>{isEditMode ? 'Edit Route' : 'Add Route'}</h3>
              <button 
                className="modal-close"
                onClick={closeAddRoute}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="source">Source</label>
                  <input
                    type="text"
                    id="source"
                    name="source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    maxLength={120}
                    title={source}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="destination">Destination</label>
                  <input
                    type="text"
                    id="destination"
                    name="destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    maxLength={120}
                    title={destination}
                    required
                  />
                </div>
                
                
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dropStation">Drop Station</label>
                  <div className="inline-input-group">
                    <input
                      type="text"
                      id="dropStation"
                      name="dropStation"
                      placeholder="Enter a drop station"
                      value={dropStationInput}
                      onChange={(e) => setDropStationInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDropStation(); } }}
                    />
                    <button type="button" className="btn" onClick={() => addDropStation()}>Add</button>
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => { setDropStations([]); setDropStationInput(''); }}
                    >
                      Clear
                    </button>
                  </div>
                  {dropStations.length > 0 && (
                    <div className="tags tag-list">
                      {dropStations.map((station, idx) => (
                        <span 
                          key={station}
                          className="tag"
                          title={station}
                        >
                          <span className="tag-index">{idx + 1}</span>
                          <span className="tag-text"><MdLocationOn style={{ display: 'inline', marginRight: '4px' }} /> {station}</span>
                          <button 
                            type="button" 
                            className="tag-remove"
                            onClick={() => removeDropStation(station)}
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="round">Round</label>
                  <select
                    id="round"
                    name="round"
                    value={round}
                    onChange={(e) => setRound(e.target.value)}
                    required
                  >
                    <option value="">Select Round</option>
                    <option value="1">Round 1</option>
                    <option value="2">Round 2</option>
                    <option value="3">Round 3</option>
                    <option value="4">Round 4</option>
                    <option value="5">Round 5</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="timing">Timing (IST)</label>
                  <div className="inline-input-group">
                    <input
                      type="time"
                      id="timing"
                      name="timing"
                    value={timing}
                    onChange={handleTimingChange}
                    required
                    />
                    <button 
                      type="button"
                      className="btn btn-secondary select-vehicle-btn"
                      onClick={handleSelectVehicleClick}
                      disabled={!timing}
                    >
                      Select Vehicle
                    </button>
                  </div>
                </div>
              </div>

              {timing && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="assignedVehicles">Assign Vehicle(s)</label>
                    <div className="vehicle-checkbox-container">
                      {vehicles && vehicles.length > 0 ? (
                        vehicles.map((v, idx) => {
                          const key = v.id || v.registrationNumber || idx;
                          const reg = v.registrationNumber || 'Unknown Reg';
                          const owner = v.ownerName ? ` - ${v.ownerName}` : '';
                          const isSelected = assignedVehicles.includes(reg);
                          return (
                            <div key={key} className="vehicle-checkbox-item">
                              <input
                                type="checkbox"
                                id={`vehicle-${key}`}
                                value={reg}
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAssignedVehicles(prev => [...prev, reg]);
                                  } else {
                                    setAssignedVehicles(prev => prev.filter(v => v !== reg));
                                  }
                                }}
                              />
                              <label htmlFor={`vehicle-${key}`} className="vehicle-checkbox-label">
                                {`${reg}${owner}`}
                              </label>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-vehicles-message">No vehicles available for this timing</div>
                      )}
                    </div>
                    {assignedVehicles.length > 0 && (
                      <div className="selected-vehicles-summary">
                        <strong>Selected: </strong>
                        <div className="tags tag-list">
                          {assignedVehicles.map((reg, idx) => (
                            <span 
                              key={reg}
                              className="tag"
                              title={reg}
                            >
                              <span className="tag-index">{idx + 1}</span>
                              <span className="tag-text">{reg}</span>
                              <button 
                                type="button" 
                                className="tag-remove"
                                onClick={() => setAssignedVehicles(prev => prev.filter(r => r !== reg))}
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              

              

              {error && (
                <div className="error-message form-error">{error}</div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn"
                  onClick={closeAddRoute}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary save-route-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Route' : 'Save Route')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showDeactivateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Deactivate Route</h3>
              <button 
                className="modal-close"
                onClick={() => { setShowDeactivateModal(false); setDeactivateData({ routeId: null, routeLabel: '', reason: '' }); }}
              >
                ×
              </button>
            </div>
            <div className="deactivate-form">
              <div className="warning-message">
                <strong>Warning:</strong> You are about to deactivate route <strong>{deactivateData.routeLabel}</strong>.
                This action will hide it from active operations.
              </div>
              <div className="form-group">
                <label htmlFor="deactivateRouteReason">
                  Reason for deactivation <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="deactivateRouteReason"
                  name="reason"
                  value={deactivateData.reason}
                  onChange={(e) => setDeactivateData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Please provide a reason for deactivating this route..."
                  rows="4"
                  className="deactivate-reason-textarea"
                  required
                />
              </div>
              {tableError && (
                <div className="error-message">{tableError}</div>
              )}
              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={() => { setShowDeactivateModal(false); setDeactivateData({ routeId: null, routeLabel: '', reason: '' }); }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-danger"
                  onClick={async () => {
                    if (!deactivateData.reason.trim()) {
                      toast.warn('Please provide a reason for deactivating the route.');
                      return;
                    }
                    try {
                      await routesApi.updateRouteStatus(deactivateData.routeId, 'INACTIVE');
                      setShowDeactivateModal(false);
                      setDeactivateData({ routeId: null, routeLabel: '', reason: '' });
                      await loadRoutes();
                    } catch (e) {
                      setTableError(e?.response?.data?.message || e?.message || 'Failed to update status');
                    }
                  }}
                >
                  Deactivate Route
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {routeToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close"
                onClick={cancelDeleteRoute}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const src = (routeToDelete.source || '').trim();
                const dest = (routeToDelete.destination || '').trim();
                const routeLabel = src && dest ? `${src} - ${dest}` : (routeToDelete.routeName || 'this route');
                return (
                  <p>Are you sure you want to delete {routeLabel}?</p>
                );
              })()}
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelDeleteRoute}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteRoute}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* removed extra Assign Vehicle modal */}
    </div>
  );
}

export default Routes;
